import React, { useState } from 'react';
import axios from 'axios';
import { DownloadSimple, Upload } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkUploadModal = ({ open, onClose, title, exportUrl, importUrl, templateName, onSuccess, instructions }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownload = () => {
    const token = localStorage.getItem('token');
    if (!token) { notify('Please log in again to download the template.'); return; }
    const link = document.createElement('a');
    link.href = `${API}${exportUrl}?token=${encodeURIComponent(token)}`;
    link.setAttribute('download', templateName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // NOTE: do NOT manually set 'Content-Type' — axios + browser must auto-generate
      // 'multipart/form-data; boundary=...' for FastAPI/Starlette to parse the upload.
      const resp = await axios.post(`${API}${importUrl}`, formData);
      setResult(resp.data);
      if ((resp.data.created || 0) > 0 || (resp.data.updated || 0) > 0) onSuccess?.();
    } catch (error) {
      // Surface the actual reason so the user knows what went wrong.
      let messages = [];
      const resp = error.response;
      if (!resp) {
        messages.push(`Network error: could not reach the server (${error.message || 'no response'}). Please check your connection and try again.`);
      } else if (resp.status === 401 || resp.status === 403) {
        messages.push(`Permission denied (HTTP ${resp.status}). You may need to log out and back in. Bulk import is restricted to Vice President and President.`);
      } else if (resp.status === 413) {
        messages.push('File is too large. Please split it into smaller batches and try again.');
      } else {
        const detail = resp.data?.detail;
        if (typeof detail === 'string') {
          messages.push(detail);
        } else if (Array.isArray(detail)) {
          // FastAPI validation errors come as a list of dicts
          messages = detail.map((d) =>
            typeof d === 'string' ? d : `${(d.loc || []).join('.')}: ${d.msg || JSON.stringify(d)}`
          );
        } else {
          messages.push(`Upload failed (HTTP ${resp.status})`);
        }
      }
      console.error('[BulkUpload] failed', resp?.status, resp?.data || error.message);
      setResult({ created: 0, updated: 0, errors: messages });
    } finally { setUploading(false); }
  };

  const handleClose = () => { setFile(null); setResult(null); onClose(); };
  const totalSuccess = (result?.created || 0) + (result?.updated || 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-4 bg-[#F4F9F7] rounded-xl border border-[#D5E8E4]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-sm font-bold">1</div>
              <h4 className="text-sm font-semibold text-[#052A23]">Download Template</h4>
            </div>
            <p className="text-xs text-[#6D8E87] mb-3 ml-10">{instructions || 'CSV template with column headers. Fill in your data and upload.'}</p>
            <button onClick={handleDownload}
              className="ml-10 flex items-center gap-2 px-4 py-2 bg-white border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#00a69c] hover:bg-[#E2F3F0] transition"
              data-testid="bulk-download-btn">
              <DownloadSimple size={16} weight="bold" /> Download CSV Template
            </button>
          </div>

          <div className="p-4 bg-[#F4F9F7] rounded-xl border border-[#D5E8E4]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-sm font-bold">2</div>
              <h4 className="text-sm font-semibold text-[#052A23]">Upload Filled CSV</h4>
            </div>
            <div className="ml-10">
              <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white" data-testid="bulk-file-input" />
            </div>
          </div>

          {result && (
            <div className={`p-4 rounded-xl border ${totalSuccess > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="bulk-result">
              <div className="flex items-center gap-3 mb-1">
                <span className={`text-sm font-bold ${totalSuccess > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {result.created > 0 && `${result.created} created`}
                  {result.updated > 0 && `${result.created > 0 ? ', ' : ''}${result.updated} updated`}
                  {totalSuccess === 0 && 'No records processed'}
                </span>
              </div>
              {result.warnings?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto p-2 rounded bg-amber-50 border border-amber-200" data-testid="bulk-warnings">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Warnings ({result.warnings.length}):</p>
                  {result.warnings.map((w, i) => <p key={i} className="text-xs text-amber-700">{w}</p>)}
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto">
                  {result.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={handleClose} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Close</button>
            <button onClick={handleUpload} disabled={!file || uploading}
              className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] transition disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="bulk-upload-btn">
              <Upload size={16} weight="bold" />
              {uploading ? 'Processing...' : 'Upload & Import'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
